import { foldTag, parseTagPath, type TagOption } from '@/domain/item-tags';
import { m, type Message } from '@/i18n';
import { fuzzyRank } from '@/lib/fuzzy';

/** One row of the tag picker: a ranked candidate (with the matched characters)
 *  or the trailing "Create #typed" row. */
export type TagPickerRow =
  | { kind: 'tag'; option: TagOption; positions: number[] }
  | { kind: 'create'; tag: string };

/**
 * The tag picker's rows for a query (docs/FIELDS_AND_TAGS.md §6.4). Pure.
 *
 *   - Empty: the top of the ranked list, in the server's order (nearest and
 *     freshest first).
 *   - Ending in `/` (`area/`): that node's descendants, in ranked order.
 *   - Otherwise: a fuzzy pass over every candidate's spelling - the
 *     did-you-mean, so `techdebt` offers `tech-debt` first.
 *
 * A "Create #typed" row trails the hits when the typed text is a valid path
 * that no candidate (and no worn tag) already folds to: similarity comes
 * first, a new tag last. Worn tags are never offered. A query naming a
 * candidate's exact path puts that candidate first.
 */
export function tagPickerRows(
  query: string,
  options: readonly TagOption[],
  worn: ReadonlySet<string>,
  limit = 8,
): TagPickerRow[] {
  const q = query.trim().replace(/^#/, '');
  const offered = options.filter((o) => !worn.has(o.fold));
  const asRow = (option: TagOption, positions: number[] = []): TagPickerRow => ({
    kind: 'tag',
    option,
    positions,
  });
  let rows: TagPickerRow[];
  if (q === '') {
    rows = offered.slice(0, limit).map((o) => asRow(o));
  } else if (q.endsWith('/')) {
    const prefix = foldTag(q);
    rows = offered
      .filter((o) => o.fold.startsWith(prefix))
      .slice(0, limit)
      .map((o) => asRow(o));
  } else {
    rows = fuzzyRank(q, offered, (o) => o.tag)
      .slice(0, limit)
      .map((r) => asRow(r.item, r.positions));
  }
  const parsed = parseTagPath(q);
  // A query that IS a candidate's path puts that candidate first, so Enter on
  // a typed (or prefilled) path picks exactly that tag.
  const exact = parsed.ok
    ? rows.findIndex((r) => r.kind === 'tag' && r.option.fold === parsed.value.fold)
    : -1;
  if (exact > 0) rows.unshift(...rows.splice(exact, 1));
  else if (exact < 0 && parsed.ok) {
    const option = offered.find((o) => o.fold === parsed.value.fold);
    if (option) rows = [asRow(option), ...rows].slice(0, limit);
  }
  if (
    parsed.ok &&
    !worn.has(parsed.value.fold) &&
    !options.some((o) => o.fold === parsed.value.fold)
  ) {
    rows.push({ kind: 'create', tag: parsed.value.tag });
  }
  return rows;
}

/** The quiet adoption line of a row ("on 12 items · 3 teams"), as a
 *  descriptor the surface formats. */
export function tagAdoptionMessage(option: TagOption): Message {
  const items = m('pickers.tag.onItems', { count: option.count });
  return option.teams > 1 ? m('pickers.tag.withTeams', { items, count: option.teams }) : items;
}
